/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IImageSettings, Text } from '@msdyn365-commerce/core';
import { AddToWishlistComponent, IWishlistActionErrorResult } from '@msdyn365-commerce/components';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {
    getConfigureErrors,
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonResources,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxResources as IBuyboxExtentionResources,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { desktopLayout, IBuyboxProps as IBuyboxExtentionProps, IBuyboxProps, leftColumnSize } from '../definition-extensions/buybox.ext.props.autogenerated';
import { renderAddToOrderTemplateButton } from './components/buybox-add-to-order-template';
import { IBuyboxData } from './buybox.data';
import { BazaarVoiceReviewOverview, BazaarVoiceStars } from '../../../components/review.view';
import IncrementalQuantity from './components/incremental-quantity';
import { Image } from '@msdyn365-commerce/core';
import { IMediaData, MediaGallery } from '@msdyn365-commerce-modules/media-gallery';

// Monkey patch the image render to fix bugged behavior with fallBackSrc
if (!(Image.prototype as any).__patched__) {
    (Image.prototype as any).__patched__ = '__patched__';
    const originalRender = Image.prototype.render;
    Image.prototype.render = function (...args) {
        if (this.props?.fallBackSrc === 'empty') (this.props as any).fallBackSrc = '';
        return originalRender.apply(this, args);
    };
}

// Monkey patch the media gallery to make the video available outside of modal
if (!(MediaGallery.prototype as any).__patched__) {
    (MediaGallery.prototype as any).__patched__ = '__patched__';
    const original_renderCarouselItemImageView = (MediaGallery.prototype as any)._renderCarouselItemImageView;
    (MediaGallery.prototype as any)._renderCarouselItemImageView = function (
        ...args: [string, IMediaData, IImageSettings, number, boolean?]
    ) {
        const [zoomView, media, imageSettings, index] = args;
        args = [zoomView, media, imageSettings, index, true];
        return original_renderCarouselItemImageView.apply(this, args);
    };
}

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

/**
 * RenderQuantity.
 * @param quantityComponent - QuantityComponent.
 * @param callbacks - Callbacks.
 * @param props - Props.
 * @param state - State.
 * @param extentionResources - ExtentionResources.
 * @param quantityLimitsMessages - QuantityLimitsMessages.
 * @param max - Max.
 * @param telemetryContent - TelemetryContent.
 * @param unitOfMeasure - Unit Of Measure.
 * @returns -- Returns.
 */
const renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    max: number | undefined,
    telemetryContent?: ITelemetryContent,
    unitOfMeasure?: React.ReactNode
): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, LabelContainerProps, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps} className='ms-buybox__product-quantity-label ms-buybox__product-quantity-label-heading'>
                {resources.productQuantityHeading}
                {errors}
            </Node>

            <IncrementalQuantity
                id={LabelContainerProps.htmlFor}
                max={max}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            />
            {unitOfMeasure}
            {quantityLimitsMessages}
        </Node>
    );
};

/**
 * Render key in price function.
 * @param keyInPrice - KeyInPrice.
 * @returns -- Returns.
 */
const renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

/**
 * RenderAddToCart.
 * @param addToCart - AddToCart.
 * @returns -- Returns.
 */
const renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

/**
 * RenderFindInStore.
 * @param findInStore - FindInStore.
 * @returns -- Returns.
 */
const renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * RenderAddToOrderTemplate.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToOrderTemplate = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToOrderTemplate || !product) {
        return null;
    }

    const orderTemplateButton = renderAddToOrderTemplateButton(props, state, callbacks, product);

    return (
        <Node {...props.addToOrderTemplate.ContainerProps}>
            {props.addToOrderTemplate.errorBlock}
            {orderTemplateButton}
        </Node>
    );
};

/**
 * Add to wishlist failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToWishlistFailed = (callbacks: IBuyboxCallbacks, resources: IBuyboxCommonResources, product: SimpleProduct | undefined) => (
    result: IWishlistActionErrorResult
) => {
    callbacks.updateErrorState({
        errorHost: 'WISHLIST',
        configureErrors:
            result.status === 'MISSINGDIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param product - Product data.
 * @returns -- Returns.
 */
const renderAddtoWishlistButton = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    product: SimpleProduct
): React.ReactNode => {
    const { context, resources } = props;
    const wishlists = props.data.wishlists.result;
    const isShowWishlitButtonText = true;
    return (
        <AddToWishlistComponent
            className='msc-add-to-cart-extra-actions'
            addToWishlistButtonText={resources.addToWishlistButtonText}
            removeFromWishlistButtonText={resources.removeFromWishlistButtonText}
            addToWishlistMessage={resources.addToWishlistMessage}
            removedFromWishlistMessage={resources.removedFromWishlistMessage}
            addItemToWishlistError={resources.addItemToWishlistError}
            removeItemFromWishlistError={resources.removeItemFromWishlistError}
            nameOfWishlist={resources.nameOfWishlist}
            data={{ product, wishlists }}
            context={context}
            ariaRole='button'
            id={props.id}
            typeName={props.typeName}
            onError={onAddToWishlistFailed(callbacks, resources, product)}
            getSelectedProduct={state.selectedProduct}
            showButtonText={isShowWishlitButtonText}
            showButtonTooltip={false}
        />
    );
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToWishlist = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToWishlist || !product) {
        return null;
    }

    if (!props.config.enableWishlist) {
        return null;
    }

    const wishlistButton = renderAddtoWishlistButton(props, state, callbacks, product);
    return (
        <Node {...props.addToWishlist.ContainerProps}>
            {props.addToWishlist.errorBlock}
            {wishlistButton}
        </Node>
    );
};

/**
 * RRenderShopSimilarItem.
 * @param shopSimilarItem - ShopSimilarItem.
 * @returns -- Returns.
 */
const renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

/**
 * RenderSocialShare.
 * @param socialShare - Social share react node array.
 * @returns -- Returns.
 */
const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};

/**
 * BuyboxView.
 * @param props - Props.
 * @returns -- Returns.
 */
const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>> = props => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const {
        ModuleProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        MediaGalleryContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        productComparisonButton,
        configure,
        description,
        findInStore,
        quantity,
        price,
        title,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        shopSimilarDescription,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        max,
        unitOfMeasure,
        callbacks,
        state,
        resources,
        quantityLimitsMessages,
        telemetryContent,
        app: { config: appConfig },
        catalogs
    } = props;
    let skuText: string = props.resources.skuText;
    skuText += props.data.product.result?.ItemId;

    const descriptionNode = (node: React.ReactNode): string => {
        if (!node) {
            return '';
        }
        const nodeEl = node as JSX.Element;
        const DescriptionObject = nodeEl.props.children;
        return DescriptionObject;
    };

    const scrollToReviews = (): void => {
        const offsetTop: number | undefined = document.getElementById('product-customer-reviews')?.offsetTop;
        if (offsetTop) {
            window.scrollTo({ top: offsetTop, behavior: 'smooth' });
        }
    };

    const currencyFormat = (num: number): string => {
        return '$' + num.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    };

    const productSize = props.data.productSpecificationData.result?.find(result => result.Name === appConfig.productSizeAttributeName);
    const brandName = props.data.productSpecificationData.result?.find(result => result.Name === appConfig.brandNameAttributeName);
    const productUnit = props.data.productSpecificationData.result?.find(result => result.Name === appConfig.productUnitAttributeName);
    const productIngredient = props.data.productSpecificationData.result?.find(
        result => result.Name === appConfig.productIngredientAttributeName
    );
    const msrp: number | undefined = props.data.productSpecificationData.result?.find(result => result.Name === 'MSRP')?.FloatValue;

    const renderMediaGallery = () => {
        return <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
    }

    const renderBuybox = () => {
        return <div className='ms-buybox__content'>
            <p className='ms-buybox__ratings-section-brand-name'>
                <span>{brandName?.TextValue}</span>
                <span>{catalogs && catalogs[0].Name}</span>
            </p>
            {title}
            <div onClick={scrollToReviews}>
                <BazaarVoiceStars
                    context={props.context.actionContext}
                    productId={props.data.product.result?.ItemId}
                    withCount={true}
                />
            </div>
            <div className='msc-buybox__ratings-section-product-size'>
                <span className='msc-buybox__ratings-section-sku-text'>
                    {props.resources.sizeText} {productSize?.FloatValue} {productUnit?.TextValue}
                </span>
            </div>
            <Node className='msc-buybox__ratings-section'>
                <div className='msc-buybox__ratings-section-sku-text'>{skuText}</div>
            </Node>

            {!!msrp && (
                <div className='msc-buybox__msrp-section'>
                    <div className='msc-buybox__msrp-section-text'>MSRP</div>
                    {currencyFormat(msrp)}
                </div>
            )}

            <Node className='msc-buybox__price-section'>
                <div className='msc-buybox__price-section-text'>{props.resources.priceText}</div>
                {price}
            </Node>
            {configure && renderConfigure(configure)}
            {keyInPrice && renderKeyInPrice(keyInPrice)}
            <Node className='msc-buybox__bulk-purchase-section'>
                <div className='msc-buybox__bulk-purchase-button-text'>
                    {quantity &&
                        renderQuantity(
                            quantity,
                            callbacks,
                            props,
                            state,
                            resources,
                            quantityLimitsMessages,
                            max,
                            telemetryContent,
                            unitOfMeasure
                        )}
                    {props.bulkPurchaseLink}
                </div>
            </Node>
            {inventoryLabel}
            {renderAddToCart(addToCart)}
            {findInStore && renderFindInStore(findInStore)}
            {addToWishlist && renderAddToWishlist(props, state, callbacks)}
            {productComparisonButton}
            {addToOrderTemplate && renderAddToOrderTemplate(props, state, callbacks)}
            {_renderSocialShare(props.slots && props.slots.socialShare)}
        </div>
    }

    const renderProductEnrich = () => {
        return <div className='ms-buybox__product-enrichment'>
            {ArrayExtensions.hasElements(props.slots.productEnrichment)
                ? props.slots.productEnrichment[0]
                : null}
        </div>
    }

    const renderProductSpec = () => {
        return <div className='msc-buybox__product-specification-wrapper'>
            <Node className='msc-buybox__shop-description'>
                {shopSimilarLook && renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && renderShopSimilarItem(shopSimilarDescription)}
            </Node>
            <Node className='msc-buybox__description-section'>
                <div className='msc-buybox__description-section-text'>{props.resources.descriptionText}</div>
                <Text className='ms-buybox__product-description' text={descriptionNode(description)}></Text>
                {productIngredient && productIngredient.TextValue !== '' && (
                    <Text className='ms-buybox__product-description' text={productIngredient?.TextValue}></Text>
                )}
            </Node>
            {ArrayExtensions.hasElements(props.slots.productSpecification) ? (
                <Node className='msc-buybox__product-specification'>{props.slots.productSpecification[0]}</Node>
            ) : null}
        </div>
    }

    const renderReviews = () => {
        return <div id='product-customer-reviews'>
            <BazaarVoiceReviewOverview
                context={props.context.actionContext}
                productId={props.data.product.result?.ItemId}
                productName={props.data.product.result?.Name}
            />
        </div>
    }

    const classNames = ["ms-buybox"];
    switch (props.config.desktopLayout) {
        case desktopLayout.topEnrich1:
            classNames.push("top-enrich-1");
            break;
        case desktopLayout.topEnrich2:
            classNames.push("top-enrich-2");
            break;
        case desktopLayout.fullMedia:
            classNames.push("full-media");
            break;
        case desktopLayout.stacked:
            classNames.push("stacked");
    }

    switch (props.config.leftColumnSize) {
        case leftColumnSize.narrow:
            classNames.push("narrow");
            break;
        case leftColumnSize.even:
            break;
        case leftColumnSize.wide:
        default:
            classNames.push("wide");
    }

    if (props.config.reverseOrder) {
        classNames.push("reverse");
    }

    return (
        <Module {...ModuleProps}>
            {renderMediaGallery()}
            {renderBuybox()}
            {renderProductEnrich()}
            {renderProductSpec()}
            {renderReviews()}
        </Module>
    );
};

export default BuyboxView;
